import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CalendarClock, ShieldOff } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import SpotIncentive, {
  SPOT_INCENTIVE_EMBEDDED_TITLES,
  type SpotIncentiveEmbeddedView,
} from "@/react/components/editors/incentive/spotIncentive/SpotIncentive";
import { IncentiveSettingsGearButton } from "@/react/components/editors/incentive/spotIncentive/IncentiveSettingsGearButton";
import {
  buildSpotIncentivePrefill,
  prepareFileForSpotIncentive,
  type SpotIncentivePrefill,
} from "@/react/components/entityViews/files/utils/spotIncentiveFileUtils";
import {
  checkIncentivePermission,
  isTaskScheduledInPastOrToday,
} from "@/other/utils/incentiveUtils.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";
import { useFileDetailModal } from "../context/FileDetailModalContext";

export const AdjustPointsPanel = () => {
  const { file, project, entityRecord, handleCloseAdjustPointsPanel } = useFileDetailModal();
  const [prefill, setPrefill] = useState<SpotIncentivePrefill | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [record, setRecord] = useState<any>(null);
  const [embeddedView, setEmbeddedView] = useState<SpotIncentiveEmbeddedView>("form");

  const hasManagePermission = useMemo(
    () =>
      checkIncentivePermission({
        project,
        permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS,
      }),
    [project],
  );

  const isTaskEligible = useMemo(() => {
    if (!file || !project) return false;
    const task = file.task != null ? project.extraTaskStore.getById(file.task) : undefined;
    return Boolean(task && isTaskScheduledInPastOrToday(task));
  }, [file, project]);

  const canAdjustPoints = hasManagePermission && isTaskEligible;

  useEffect(() => {
    let cancelled = false;
    setPrefill(null);
    setRecord(null);
    setEmbeddedView("form");

    const setup = async () => {
      if (!canAdjustPoints || !file || !project?.spotIncentiveStore) return;
      const fileData = await prepareFileForSpotIncentive(file);
      if (!fileData || cancelled) return;
      const task = file.task != null ? project.extraTaskStore.getById(file.task) : undefined;
      const nextPrefill = buildSpotIncentivePrefill({
        file,
        fileData,
        task,
        project,
        entityRecord,
      });
      if (cancelled) return;
      setRecord(project.spotIncentiveStore.createRecord({}));
      setPrefill(nextPrefill);
    };

    void setup();
    return () => {
      cancelled = true;
    };
  }, [canAdjustPoints, file, project, entityRecord]);

  const handleBack = () => {
    if (embeddedView !== "form") {
      setEmbeddedView("form");
      return;
    }
    handleCloseAdjustPointsPanel();
  };

  const headerTitle = SPOT_INCENTIVE_EMBEDDED_TITLES[embeddedView];

  const blockedContent = !hasManagePermission ? (
    <div className="flex h-full w-full items-center justify-center p-6">
      <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-4 rounded-2xl border border-dashed border-border/60 bg-muted/30 px-6 py-10 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <ShieldOff className="size-6" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-lg font-normal tracking-tight text-foreground">
            Cannot adjust points
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            You do not have permission to adjust points on this board.
          </p>
        </div>
      </div>
    </div>
  ) : !isTaskEligible ? (
    <div className="flex h-full w-full items-center justify-center p-6">
      <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-4 rounded-2xl border border-dashed border-border/60 bg-muted/30 px-6 py-10 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <CalendarClock className="size-6" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-lg font-normal tracking-tight text-foreground">
            Cannot adjust points
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            No scheduled events have started for this task. Points can only be adjusted once
            at least one event has begun.
          </p>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-1.5 pb-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 rounded-full"
          onClick={handleBack}
          aria-label={embeddedView === "form" ? "Back to details" : "Back"}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-xl font-normal tracking-tight text-foreground">
            {headerTitle}
          </h2>
        </div>
        {embeddedView === "points" ? <IncentiveSettingsGearButton /> : null}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {blockedContent ? (
          blockedContent
        ) : record && prefill ? (
          <SpotIncentive
            key={file?.id}
            layout="embedded"
            embeddedView={embeddedView}
            onEmbeddedViewChange={setEmbeddedView}
            record={record}
            onClose={handleCloseAdjustPointsPanel}
            selectedTask={prefill.selectedTask}
            initialFileId={prefill.initialFileId}
            initialFileData={prefill.initialFileData}
            isCreatingUnreleasedRecord
            initialName={prefill.initialName}
            initialStartDate={prefill.initialStartDate}
            initialEndDate={prefill.initialEndDate}
            initialResourceIds={prefill.initialResourceIds}
          />
        ) : null}
      </div>
    </div>
  );
};
